import { useState, useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';

/**
 * Some browsers may report slight inconsistensies when reporting the
 * scrollLeft propery. As such we allow for a slight error margin when
 * determining if we're at the edges.
 *
 * E.g. scrollLeft + clientWidth = 150.555, scrollWidth = 151.
 */
const ERROR_THRESHOLD_PX = 5;

/**
 * used to determine whether the tablist is scrolled all the way to the left or
 * the right.
 */
function useScrollState(tabWrapperRef) {
  const [isAtLeft, setAtLeft] = useState(true);
  const [isAtRight, setAtRight] = useState(true);
  useEffect(() => {
    const initiallyActiveButton = tabWrapperRef.current?.querySelector(`button[aria-selected="true"]`);
    const tabWrapper = tabWrapperRef.current;
    if (!initiallyActiveButton || !tabWrapper) {
      return;
    }
    tabWrapper.scrollLeft = initiallyActiveButton.offsetLeft - initiallyActiveButton.clientWidth / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const tabWrapper = tabWrapperRef.current;
    if (!tabWrapper) {
      return;
    }
    const handleUpdate = () => {
      if (tabWrapper.scrollWidth === tabWrapper.clientWidth) {
        setAtLeft(true);
        setAtRight(true);
        return;
      }
      setAtLeft(tabWrapper.scrollLeft < ERROR_THRESHOLD_PX);
      setAtRight(tabWrapper.scrollLeft + tabWrapper.clientWidth + ERROR_THRESHOLD_PX >= tabWrapper.scrollWidth);
    };
    handleUpdate();
    const throttled = throttleToAnimationFrame(handleUpdate);
    tabWrapper.addEventListener("scroll", throttled);
    const observer = new ResizeObserver(throttled);
    observer.observe(tabWrapper);
    return () => {
      tabWrapper.removeEventListener("scroll", throttled);
      observer.disconnect();
    };
  }, [setAtLeft, setAtRight, tabWrapperRef]);
  return {
    isAtLeft,
    isAtRight
  };
}

export { useScrollState };
//# sourceMappingURL=hook.scroll-state.js.map
